import React, { useEffect, useMemo, useRef, useState } from "react";
import AddUpdateConfiguration from "./AddUpdateConfiguration";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import DateRangePicker from "../../../../../common/BillingDatePicker";
import CustomPagination from "../../../../../common/Pagination";
import TableRowsPerPage from "../../../../../common/TableRowsPerPage";
import {
  BILLING_DASHBOARD_CASE_HEADERS,
  BILLING_DASHBOARD_HEADERS,
} from "../../actions/constant";
import BillingDataTable from "../BillingDataTable";
import {
  getBillingConfigurationGrid,
  getBillingSummary,
  getCaseLevelBillingList,
  getCaseLevelMasterBillingList,
} from "../../../../../services/BillingConfigApi";
import useAnimatedCounter from "../../../../ToDos/useAnimatedCounter";
import { getTeamsBillingSearchList } from "../../../../../services/BillingAPI";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/Store";
import UpdateTeamBillingConfig from "./UpdateTeamBillingConfig";
import ViewCaseBillingConfig from "./ViewCaseBillingConfig";
import { BILLING_SORT_KEY_MAP } from "../../../Teams/action/constant";

const ConfigurationDashboard = ({
  triggerAdd,
  setTriggerAdd,
  activeTab,
  onActiveCardChange,
}: any) => {
  const HIDDEN_COLUMNS_BY_CARD: Record<string, string[]> = {
    "Master Billing List": ["teamName", "clientInformation", "caseId"],
    "Team Billing List": ["caseId", "rate"],
  };

  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeCard, setActiveCard] = useState<string>("Master Billing List");

  const [activeFilter, setActiveFilter] = useState("SYSTEM");
  const [action, setAction] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [configurationData, setConfigurationData] = useState<any[]>([]);
  const [selectedConfigurationData, setSelectedConfigurationData] =
    useState<any>(null);

  const [masterBillingCount, setMasterBillingCount] = useState<string>("");
  const [teamBillingCount, setTeamBillingCount] = useState<string>("");
  const [caseBillingCount, setCaseBillingCount] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [totalRows, setTotalRows] = useState<any>("6");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("DESC");

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([firstDayOfMonth, lastDayOfMonth]);

  const getApiSortColumn = (
    uiKey: string,
    billingType: "SYSTEM" | "TEAM" | "CASE",
  ) => {
    return BILLING_SORT_KEY_MAP[billingType]?.[uiKey] || "";
  };

  const handleDateRangeChange = (newRange: [Date | null, Date | null]) => {
    const [start, end] = newRange;
    setDateRange([start ?? undefined, end ?? undefined]);
  };

  const handleResetDate = () => {
    setDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const toLocalISOString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getBilingConfiurationData = async (
    query: string = "",
    action: string = "",
    pageNo: number = 1,
    pageSize: number = itemsPerPage,
    key: string = sortColumn,
    direction: string = sortOrder,
  ) => {
    try {
      setLoading(true);

      const trimmedQuery = query?.trim();
      const [start, end] = dateRange;

      console.log(trimmedQuery, "trimmedQuery");

      const payload: any = {
        searchString: trimmedQuery || "",
        loggedInUserId: loggedInUserid,
        pageNo,
        pageSize,
        sortColumn: key,
        sortOrder: direction,

        ...(activeCard !== "Case Billing List" && {
          startDate: start ? toLocalISOString(start) : null,
          endDate: end ? toLocalISOString(end) : null,
        }),

        ...(activeCard === "Team Billing List" && { teamId: null }),
        ...(activeCard === "Case Billing List" && { caseInfoId: null }),
      };

      if (activeCard === "Master Billing List") {
        const response = await getBillingConfigurationGrid(payload);
        setConfigurationData(response);
        setTotalPages(response[0]?.totalPages || 1);
        setTotalRows(response[0]?.totalRecords || null);
      }

      // if (activeCard === "Team Billing List") {
      //   const response = await getTeamsBillingSearchList(payload);
      //   setConfigurationData(response);
      //   setTotalPages(response[0]?.totalPages || 1);
      //   setTotalRows(response[0]?.totalRecords || null);
      // }

      // if (activeCard === "Case Billing List") {
      //   const response = await getCaseLevelMasterBillingList(payload);
      //   setConfigurationData(response);
      //   setTotalPages(response[0]?.totalPages || 1);
      //   setTotalRows(response[0]?.totalRecords || null);
      // }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const [start, end] = dateRange;

    if (start && end) {
      getBilingConfiurationData(
        searchQuery,
        activeFilter,
        currentPage,
        itemsPerPage,
        sortColumn,
        sortOrder,
      );
    }
  }, [
    dateRange,
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
  ]);

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleInputChange = debounce((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  }, 300);

  const handleResetFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setSearchQuery("");
  };

  useEffect(() => {
    if (!activeFilter) return;
    const [start, end] = dateRange;
    const filterQuery = searchQuery || "";
    getBilingConfiurationData(
      filterQuery,
      activeFilter,
      1,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
  }, [activeFilter, dateRange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target === inputRef.current) {
        return;
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        setDateRange([undefined, undefined]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    getBilingConfiurationData(
      searchQuery,
      activeFilter,
      pageNumber,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
  };

  const handleSortAction = (row: any) => {
    const apiSortColumn = getApiSortColumn(
      row.key,
      activeFilter as "SYSTEM" | "TEAM" | "CASE",
    );

    setSortColumn(apiSortColumn);
    setSortOrder(row.direction);
    getBilingConfiurationData(
      searchQuery,
      activeFilter,
      currentPage,
      itemsPerPage,
      apiSortColumn,
      row.direction,
    );
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows,
      )} of ${totalRows} entries`;

  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setSelectedConfigurationData(row);
    setOpenDrawer(true);
    setTriggerAdd(1);
  };

  useEffect(() => {
    setOpenDrawer(false);
    if (activeTab === "Configuration") {
      setActiveCard("Master Billing List");
      setActiveFilter("SYSTEM");
    }
  }, [activeTab]);

  useEffect(() => {
    if (triggerAdd) {
      setOpenDrawer(true);
    }
  }, [triggerAdd]);

  const handleClose = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setSearchQuery("");
    setOpenDrawer(false);
    setTriggerAdd(0);
    setSelectedConfigurationData(null);
    getBilingConfiurationData(
      "",
      activeFilter,
      1,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
  };

  const filteredHeaders = useMemo(() => {
    const hiddenKeys = HIDDEN_COLUMNS_BY_CARD[activeCard] || [];

    return BILLING_DASHBOARD_HEADERS.filter(
      (header) => !hiddenKeys.includes(header.key),
    );
  }, [activeCard]);

  return (
    <div>
      {!openDrawer ? (
        <div className="main_wrapper_section appoinment_wrapper mt-3">
          <Container fluid>
            <div className="container-fluid px-2">
              <div className="mt-3 mb-3">
                <Row className="align-items-center justify-content-between mb-3">
                  <Col xs="auto" className="sectionTitle noborder pe-3">
                    {activeCard}
                  </Col>

                  {/* Right side: Controls */}
                  <Col xs="auto">
                    <div className="d-flex align-items-center gap-2">
                      {/* Date Picker */}
                      {activeCard != "Case Billing List" ? (
                        <div
                          className="date-picker-container appointment-data-range customDrodownControlDAtepicker"
                          style={{ width: "280px" }}
                        >
                          <DateRangePicker
                            label=""
                            onChange={handleDateRangeChange}
                            startDate={dateRange[0]}
                            endDate={dateRange[1]}
                            showResetIcon={!!dateRange[0] && !!dateRange[1]}
                            onReset={handleResetDate}
                          />
                        </div>
                      ) : null}

                      <InputGroup className="w-auto">
                        <InputGroup.Text className="px-2">
                          <span className="material-symbols-outlined text-black-50">
                            search
                          </span>
                        </InputGroup.Text>
                        <Form.Control
                          ref={inputRef}
                          aria-label="Search"
                          onChange={(e) =>
                            handleInputChange(e.target.value.trim())
                          }
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData("text").trim();
                            handleInputChange(text);
                            if (inputRef.current) inputRef.current.value = text;
                          }}
                        />
                      </InputGroup>

                      {/* Reset Button */}
                      <Button
                        className="d-flex align-items-center"
                        style={{
                          background: "none",
                          border: "none",

                          padding: "10px",
                          fontSize: "22px",
                        }}
                        onClick={handleResetFilters}
                      >
                        <span className="material-icons reset_ic0">
                          restart_alt
                        </span>
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="mb-3 container-fluid">
              <BillingDataTable
                headers={
                  activeCard == "Case Billing List"
                    ? BILLING_DASHBOARD_CASE_HEADERS
                    : filteredHeaders
                }
                rows={configurationData}
                onSortAction={handleSortAction}
                onAction={handleRowAction}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-0 mb-4 row p-0 m-0">
              <div className="col-md-12">
                <div className="row">
                  <div className="col-md-6 showingData_wrapper">
                    <span>{entriesText}</span>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex flex-row-reverse ">
                      <CustomPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={paginate}
                      />
                      <TableRowsPerPage
                        value={itemsPerPage}
                        onSelect={handleRowsPerPageChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      ) : null}
      {openDrawer &&
        activeTab === "Configuration" &&
        activeCard == "Master Billing List" && (
          <AddUpdateConfiguration
            onClose={handleClose}
            selectedData={selectedConfigurationData}
          />
        )}
      {openDrawer &&
        activeTab === "Configuration" &&
        activeCard == "Team Billing List" && (
          <UpdateTeamBillingConfig
            onClose={handleClose}
            selectedData={selectedConfigurationData}
          />
        )}
      {openDrawer &&
        activeTab === "Configuration" &&
        activeCard == "Case Billing List" && (
          <ViewCaseBillingConfig
            onClose={handleClose}
            selectedData={selectedConfigurationData}
          />
        )}
    </div>
  );
};

export default ConfigurationDashboard;
